import { prettyPrint } from '@base2/pretty-print-object';
import { kebabCase, mapKeys, startCase } from 'lodash/fp';
import { ReactNode } from 'react';
import styled from 'styled-components';

import { LinkifyExternal } from 'dashboard/components/LinkifyExternal';
import { MonoSpan } from 'dashboard/components/Typography';
import AsyncImage from 'dashboard/components-lib/AsyncImage';
import { calculateHideOrShowAttribute } from 'dashboard/modules/Resource/calculateHideOrShow';
import Attribute, { WordBreak } from 'design-system/components/Attribute';
import Sticker from 'design-system/components/Sticker';
import { Field, FieldFile, FieldSchema, FieldType, parseField } from 'lib/fields';

const CodeBlock = styled(MonoSpan)`
  white-space-collapse: preserve;
`;

interface FieldAttributeItemProps {
  field: Field;
  fieldSchema: FieldSchema;
  isRedacted?: boolean;
  hideEmptyAttributes?: boolean;
  wordBreak?: WordBreak;
  dataTest?: string;
}

const FieldAttributeItem = ({
  field,
  fieldSchema,
  isRedacted = false,
  hideEmptyAttributes = false,
  wordBreak,
  dataTest,
}: FieldAttributeItemProps) => {
  let isImage = false;
  if (field.type === FieldType.File) {
    // We map the value keys to kebab case for file type fields to ensure that the next line
    // can correctly check the mime-type because we do not always map to kebab case on the backend
    // (e.g. for accounts we maintain key inflection for fields and do not automatically kebab case).
    const kebabValue = mapKeys(kebabCase, field.value);

    // We can cast to string here because `mime-type` is always string
    const mimeType = kebabValue['mime-type'] as string;
    isImage = !!mimeType && mimeType.includes('image');
  }

  const hasRedactionPolicy =
    fieldSchema.config['redaction-policy'] != null &&
    fieldSchema.config['redaction-policy'] !== 'none';
  const hasWritePolicy =
    fieldSchema.config['write-policy'] != null && fieldSchema.config['write-policy'] !== 'none';
  const showHelpMessage = hasRedactionPolicy || hasWritePolicy;

  let parsedFieldValue: string | ReactNode = parseField(field);
  if (
    field.type === FieldType.Hash ||
    (field.type === FieldType.Array && field.value[0]?.type === FieldType.Hash)
  ) {
    // We want to render hash-type fields as a CodeBlock for readability. We check the first item of
    // array type objects, and if that first item is a hash, we render the entire array as a CodeBlock
    // as well.
    parsedFieldValue = (
      <CodeBlock>
        {prettyPrint(parsedFieldValue, {
          indent: '  ',
          inlineCharacterLimit: 40,
        })}
      </CodeBlock>
    );
  }

  const fieldLabel =
    fieldSchema?.label && fieldSchema.label !== ''
      ? fieldSchema.label
      : fieldSchema.key.split('_').join(' ');

  // linter complains if typed as FieldFile[], even though that is only possible result
  const fileList =
    !isRedacted && field.type === FieldType.Array
      ? field.value?.filter((f) => f.type === FieldType.File)
      : [];
  // only show file list if all items in Array type are files
  const canShowFileList =
    !isRedacted && field.type === FieldType.Array && field.value?.length === fileList.length;

  let attributeItemValue = null;
  if (parsedFieldValue != null) {
    if (isImage) {
      attributeItemValue = (
        <AsyncImage
          imageUrl={isRedacted ? null : (parsedFieldValue as string)}
          modalLabel={fieldLabel}
          allowModalExpand={true}
          width='100%'
        />
      );
    } else if (canShowFileList) {
      attributeItemValue = (fileList as FieldFile[]).map((f, i) => {
        return (
          <div key={`resource-account__file-wrapper-${i}`}>
            <a
              href={f.value?.url}
              key={`resource-account__file-${i}`}
              target='_blank'
              rel='noreferrer'
            >
              {f.value?.filename}
            </a>
          </div>
        );
      });
    } else {
      const parsedValue = Array.isArray(parsedFieldValue)
        ? parsedFieldValue.join(', ')
        : parsedFieldValue;
      attributeItemValue = <LinkifyExternal>{parsedValue}</LinkifyExternal>;
    }
  }

  return calculateHideOrShowAttribute(
    hideEmptyAttributes,
    parsedFieldValue,
    <Attribute
      label={fieldLabel}
      value={attributeItemValue}
      redacted={fieldSchema.config['redaction-policy'] !== 'never' && isRedacted}
      data-test={dataTest}
      wordBreak={wordBreak}
      helpText={
        showHelpMessage && (
          <>
            {hasRedactionPolicy && (
              <p>
                Redaction policy:{' '}
                <Sticker>{startCase(fieldSchema.config['redaction-policy'] ?? '')}</Sticker>
              </p>
            )}
            {hasWritePolicy && (
              <p>
                Write policy:{' '}
                <Sticker>{startCase(fieldSchema.config['write-policy'] ?? '')}</Sticker>
              </p>
            )}
          </>
        )
      }
      key={fieldSchema.key}
      placeholder='--'
    />,
  );
};

export default FieldAttributeItem;
